import { useMediaQuery } from '@mui/material';
import type { IBudgetItem } from '../types';
import BudgetTableRow from './BudgetTableRow';
import BudgetItemsTableMobile from './mobile/BudgetItemsTableMobile';

export type { IBudgetItem };

interface IBudgetItemsTableProps {
  items: IBudgetItem[];
  proposalItems?: IBudgetItem[];
  isExternalService?: boolean;
  isEditMode?: boolean;
  isPackageItemsLoading?: boolean;
  showDiscountColumn?: boolean;
  onItemChange?: (index: number, field: keyof IBudgetItem, value: string | number | boolean | undefined) => void;
  onRemoveItem?: (index: number) => void;
}

const SKELETON_ROW_COUNT = 5;

const BudgetItemsTableSkeletonDesktop = ({ columnCount }: { columnCount: number }) => (
  <>
    {Array.from({ length: SKELETON_ROW_COUNT }).map((_, rowIndex) => (
      <tr key={`skeleton-row-${rowIndex}`}>
        {Array.from({ length: columnCount }).map((_, colIndex) => (
          <td key={`skeleton-cell-${rowIndex}-${colIndex}`} className="py-3 px-2 align-middle">
            <div
              className="h-4 w-full max-w-[95%] rounded bg-gray-200 animate-pulse"
              style={{ animationDelay: `${(rowIndex + colIndex) * 40}ms` }}
            />
          </td>
        ))}
      </tr>
    ))}
  </>
);

const BudgetItemsTable = (props: IBudgetItemsTableProps) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const showDiscountColumn = props.showDiscountColumn !== false;

  if (isMobile) {
    return (
      <BudgetItemsTableMobile
        items={props.items}
        proposalItems={props.proposalItems}
        isExternalService={props.isExternalService}
        isEditMode={props.isEditMode}
        isPackageItemsLoading={props.isPackageItemsLoading}
        showDiscountColumn={showDiscountColumn}
        onItemChange={props.onItemChange}
        onRemoveItem={props.onRemoveItem}
      />
    );
  }
  
  return (
    <div className="overflow-x-auto">
      <table
        className="table table-layout-fixed table-column-overflow-hidden table-head-filled mb-3 w-full [&_td]:border-0 [&_th]:border-0 [&_tbody_tr]:border-0 [&_tbody_tr]:border-b-0 [&_thead_tr]:border-0 [&_thead_tr]:border-b-0 [&_tbody_tr_td]:border-0 [&_tbody_tr_td]:border-b-0 [&_thead_tr_th]:border-0 [&_thead_tr_th]:border-b-0"
        style={{ borderCollapse: 'separate', borderSpacing: '0 0' }}
      >
      <thead>
        <tr className="border-0 !border-b-0" style={{ borderBottom: 'none' }}>
          <th className="w-[7%] border-0 !border-b-0" style={{ borderBottom: 'none' }}>
            Ref.
          </th>
          <th className="w-[13%] border-0 !border-b-0" style={{ borderBottom: 'none' }}>
            Item
          </th>
          <th className="w-[16%] border-0 !border-b-0" style={{ borderBottom: 'none' }}>
            Descrição
          </th>
          <th className="w-[10%] border-0 !border-b-0" style={{ borderBottom: 'none' }}>
            Quantidade
          </th>
          <th className="w-[12%] border-0 !border-b-0" style={{ borderBottom: 'none' }}>
            Valor (R$)
          </th>
          <th className="w-[13%] border-0 !border-b-0" style={{ borderBottom: 'none' }}>
            Cobertura
          </th>
          <th className="w-[10%] border-0 !border-b-0" style={{ borderBottom: 'none' }}>
            Disponível em
          </th>
          {showDiscountColumn && (
            <th className="w-[10%] border-0 !border-b-0" style={{ borderBottom: 'none' }}>
              % Desconto
            </th>
          )}
          <th className="w-[15%] border-0 !border-b-0" style={{ borderBottom: 'none' }}>
            Subtotal (R$)
          </th>
          <th className="w-[4%] text-center border-0 !border-b-0" style={{ borderBottom: 'none' }}>
          </th>
        </tr>
      </thead>
      <tbody>
        {props.isPackageItemsLoading ? (
          <BudgetItemsTableSkeletonDesktop
            columnCount={showDiscountColumn ? 10 : 9}
          />
        ) : (
          props.items.map((item, index) => {
            const originalItem = 
              props.proposalItems?.[index]?.ref === item.ref
                ? props.proposalItems[index]
                : props.proposalItems?.find((proposalItem, proposalIndex) => 
                    proposalItem.ref === item.ref && proposalIndex === index
                  );
            
            return (
              <BudgetTableRow
                key={`item-${item.ref}-${index}`}
                item={item}
                originalItem={originalItem}
                index={index}
                isExternalService={props.isExternalService}
                isEditMode={props.isEditMode}
                showDiscountColumn={showDiscountColumn}
                onItemChange={props.onItemChange}
                onRemoveItem={props.onRemoveItem}
              />
            );
          })
        )}
      </tbody>
    </table>
    </div>
  );
};

export default BudgetItemsTable;
